import { useEffect } from 'react';

// A new version waits (service worker): load it at the first moment it cannot cost the operator anything, so the
// webapp does not stay on an old version after a deploy. Safe means the tab is hidden, or nothing is being typed
// (no focused or filled-in text field) and no dialog is open. Otherwise the « Update available » banner stays.

const EDITABLE = 'input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="url"], input[type="number"], textarea, [contenteditable="true"]';

/** True when reloading now would not lose anything the operator is doing */
export function safeToReload(doc: Document = document): boolean {
  if (doc.visibilityState === 'hidden') return true;
  if (doc.querySelector('[role="dialog"], dialog[open]')) return false;
  const active = doc.activeElement;
  if (active && active.matches?.(EDITABLE)) return false;
  // Typed but not sent: any text field holding something other than what the page put there
  for (const el of Array.from(doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea'))) {
    if (!el.matches(EDITABLE)) continue;
    if (el.value.trim() && el.value !== el.defaultValue) return false;
  }
  return true;
}

/** Calls `update` once a waiting version can be loaded safely: on leaving the tab, then every 30 s */
export function useSafeUpdate(waiting: boolean, update: () => void, check: () => boolean = safeToReload): void {
  useEffect(() => {
    if (!waiting) return;
    let done = false;
    const tryNow = () => {
      if (done || !check()) return;
      done = true;
      update();
    };
    tryNow();
    document.addEventListener('visibilitychange', tryNow);
    const id = window.setInterval(tryNow, 30_000);
    return () => {
      document.removeEventListener('visibilitychange', tryNow);
      window.clearInterval(id);
    };
  }, [waiting, update, check]);
}
